import { defineStore } from 'pinia'
import { LoginType } from '@/modules/public/Signin/schemas'
import { AuthResponse, UserPersona } from '@/Network/Types/Responses/AuthResponses'
import Network from '@/Network'
import { useCompanyStore } from './useCompanyStore'
import { useNotificationStore } from './useNotificationStore'
import { resetAllStores } from '@/plugins/piniaResetPlugin'
import { stopClarity } from '@/config/clarity'
import { clearAuthStorage } from '@/utils/storage'

export type AuthState = AuthResponse

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    userId: Number(localStorage.getItem('@ME:userId')) || 0,
    access_token: Network.auth.getLocalToken() || Network.auth.getLocalDoctorToken() || null,
    needChangePassword: false,
    permissions: [],
    permissionRelations: [],
    name: ''
  }),
  actions: {
    async login(data: LoginType): Promise<UserPersona | undefined> {
      if (data) {
        try {
          const companyStore = useCompanyStore()
          const authData: AuthState = await Network.auth.loginUserCompany(data)
          if (authData.persona === 'ANTECIPE_AFFILIATE') {
            // Afiliado não tem empresa: getMyCompanies com o token de afiliado daria 401.
            Network.auth.persistAffiliateAuthResponse(authData)
            this.access_token = authData.access_token
            return 'ANTECIPE_AFFILIATE'
          }
          this.access_token = authData.access_token
          this.needChangePassword = authData.needChangePassword
          this.permissions = authData.permissions
          this.permissionRelations = authData.permissionRelations
          this.name = authData.name
          this.userId = authData.userId
          Network.auth.persistAuthResponse(authData)
          await companyStore.getMyCompanies()
          return authData.persona ?? 'INTERNAL'
        } catch (e) {
          console.error('Erro ao realizar login')
          throw e
        }
      }
      return undefined
    },
    async loginDoctor(data: LoginType) {
      if (data) {
        try {
          const authData: AuthState = await Network.auth.loginDoctor(data)
          this.access_token = authData.access_token
          this.needChangePassword = authData.needChangePassword
          this.permissions = authData.permissions
          this.name = authData.name
          Network.auth.persistDoctorAuthResponse(authData)
        } catch (e) {
          console.error('Erro ao realizar login')
          throw e
        }
      }
    },

    async getAuthorizationRelations() {
      if (this.arePermissionsLoaded) {
        return Promise.resolve()
      }

      try {
        const relations = await Network.permission.listAllRelations()
        this.permissionRelations = relations
      } catch (e) {
        console.error('Erro ao buscar relações de permissão')
        throw e
      }
    },
    logout() {
      const fcmToken = localStorage.getItem('@ME:fcm_token')
      if (fcmToken) {
        Network.webNotification.unsubscribe(fcmToken).catch(() => {})
      }
      const notificationStore = useNotificationStore()
      notificationStore.stopPolling()

      stopClarity()
      clearAuthStorage()
      resetAllStores()
      window.location.href = '/'
    }
  },
  getters: {
    getUserName(): string {
      if (this.name) return this.name
      const stored = localStorage.getItem('@ME:name')
      if (!stored) return 'Minha Escala'
      try {
        return JSON.parse(stored)
      } catch {
        return stored
      }
    },
    getUserId(): number {
      return this.userId || 0
    },

    arePermissionsLoaded(): boolean {
      return this.permissionRelations !== undefined && this.permissionRelations.length > 0
    }
  }
})
